// hooks/playground/scenario/useScenarios.js
import { useState, useCallback, useEffect } from 'react';
import { useScenarioAPI } from './useScenarioAPI';
import { useUserPreference } from '../../useUserPreference';

export const useScenarios = (selectedYear, availableYears = [2025, 2026, 2027]) => {
  
  // ============================================================================
  // API LAYER
  // ============================================================================
  const {
    isLoading: isLoadingAPI,
    isSaving: isSavingAPI,
    fetchScenarios,
    fetchScenario,
    createScenario: createScenarioAPI,
    updateScenario: updateScenarioAPI,
    deleteScenario: deleteScenarioAPI,
  } = useScenarioAPI();

  // ============================================================================
  // DEFAULT SCENARIO PREFERENCE (from user preferences API)
  // ============================================================================
  const [defaultScenarioPref, setDefaultScenarioPref, loadingDefaultPref] = useUserPreference(
    'pl_playground',
    'default_scenario_id',
    null
  );

  // ============================================================================
  // STATE
  // ============================================================================
  const [currentScenarioId, setCurrentScenarioId] = useState(null);
  const [currentScenarioName, setCurrentScenarioName] = useState('Baseline');
  const [currentScenario, setCurrentScenario] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [scenariosLoaded, setScenariosLoaded] = useState(false);

  // All scenarios
  const [savedScenarios, setSavedScenarios] = useState([]);
  
  // Loading states
  const [isLoadingScenario, setIsLoadingScenario] = useState(false);
  
  // Modal states
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveModalMode, setSaveModalMode] = useState('new'); // 'new', 'saveAs', 'rename', 'edit'
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingScenarioSwitch, setPendingScenarioSwitch] = useState(null);

  // ============================================================================
  // INITIALIZATION - Load scenarios on mount
  // ============================================================================
  
  const loadScenariosList = useCallback(async () => {
    try {
      const scenarios = await fetchScenarios();
      setSavedScenarios(scenarios);
      setScenariosLoaded(true);
    } catch (error) {
      console.error('❌ Failed to load scenarios:', error);
    }
  }, [fetchScenarios]);

  useEffect(() => {
    loadScenariosList();
  }, [loadScenariosList]);

  // ============================================================================
  // STATE HELPERS
  // ============================================================================
  const markAsChanged = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const markAsSaved = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  const isBaseline = !currentScenarioId || currentScenarioName === 'Baseline' || currentScenarioName === 'Actual Data';

  // ============================================================================
  // LOAD/SWITCH SCENARIOS
  // ============================================================================
  const loadActual = useCallback((clearAdjustmentsFn, resetCustomItemsFn) => {
    setCurrentScenarioId(null);
    setCurrentScenarioName('Actual Data');
    setCurrentScenario(null);
    setHasUnsavedChanges(false);
    
    if (clearAdjustmentsFn) clearAdjustmentsFn();
    if (resetCustomItemsFn) resetCustomItemsFn();
  }, []);

  const loadScenario = useCallback(async (scenarioId, applyScenarioFn) => {
    setIsLoadingScenario(true);
    
    try {
      const scenario = await fetchScenario(scenarioId);
      
      setCurrentScenarioId(scenario.scenario_id);
      setCurrentScenarioName(scenario.scenario_name);
      setCurrentScenario(scenario);
      setHasUnsavedChanges(false);  // ← This is correct!
      
      if (applyScenarioFn) {
        applyScenarioFn({
          averagingMethod: scenario.averaging_method,
          adjustments: scenario.adjustments,
          customItems: scenario.custom_items,
        });
      }
    } catch (error) {
      console.error('❌ Error loading scenario:', error);
      alert(`Failed to load scenario: ${error.message}`);
    } finally {
      setIsLoadingScenario(false);
    }
  }, [fetchScenario]);

  const handleSelectScenario = useCallback((scenarioId) => {
    if (hasUnsavedChanges) {
      console.log('⚠️ [useScenarios] Has unsaved changes - showing dialog');
      setPendingScenarioSwitch(scenarioId);
      setShowUnsavedDialog(true);
      return null;
    } else {
      console.log('✅ [useScenarios] No unsaved changes - returning action');
      if (scenarioId === null) {
        return { action: 'loadActual' };
      } else {
        return { action: 'loadScenario', scenarioId };
      }
    }
  }, [hasUnsavedChanges, showUnsavedDialog]);

  const newScenario = useCallback((clearAdjustmentsFn, resetCustomItemsFn) => {
    setCurrentScenarioId(null);
    setCurrentScenarioName('Untitled Scenario');
    setCurrentScenario(null);
    setHasUnsavedChanges(true);
    
    if (clearAdjustmentsFn) clearAdjustmentsFn();
    if (resetCustomItemsFn) resetCustomItemsFn();
  }, []);

  // ============================================================================
  // CREATE SCENARIO (with optimistic update)
  // ============================================================================
  const createScenario = useCallback(async ({ name, description, averagingMethod, adjustments, customItems }) => {
    const scenarioData = {
      scenario_name: name,
      scenario_description: description,
      averaging_method: averagingMethod,
      adjustments: adjustments,
      custom_items: customItems,
    };

    // 🎯 OPTIMISTIC UPDATE - Create temp scenario
    const tempScenario = {
      scenario_id: `temp_${Date.now()}`,
      ...scenarioData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: 'current-user',
      _isOptimistic: true,
    };

    setCurrentScenarioId(tempScenario.scenario_id);
    setCurrentScenarioName(tempScenario.scenario_name);
    setCurrentScenario(tempScenario);
    setSavedScenarios(prev => [...prev, tempScenario]);
    markAsSaved();
    setShowSaveModal(false);

    try {
      const realScenario = await createScenarioAPI(scenarioData);
      
      setCurrentScenarioId(realScenario.scenario_id);
      setCurrentScenario(realScenario);
      setSavedScenarios(prev => 
        prev.map(s => s.scenario_id === tempScenario.scenario_id ? realScenario : s)
      );
      
      return realScenario;
    } catch (error) {
      console.error('❌ Error creating scenario:', error);
      
      setSavedScenarios(prev => prev.filter(s => s.scenario_id !== tempScenario.scenario_id));
      setCurrentScenarioId(null);
      setCurrentScenarioName('Untitled Scenario');
      setCurrentScenario(null);
      
      alert(`Failed to save scenario: ${error.message}`);
      throw error;
    }
  }, [createScenarioAPI, markAsSaved]);

  // ============================================================================
  // UPDATE SCENARIO (with optimistic update)
  // ============================================================================
  const updateScenario = useCallback(async ({ name, description, averagingMethod, adjustments, customItems }) => {
    if (!currentScenarioId || currentScenarioId.startsWith('temp_')) {
      console.warn('⚠️ Cannot update - no valid scenario ID');
      return createScenario({ name, description, averagingMethod, adjustments, customItems });
    }

    const updates = {
      scenario_name: name || currentScenarioName,
      scenario_description: description !== undefined ? description : currentScenario?.scenario_description,
      averaging_method: averagingMethod,
      adjustments: adjustments,
      custom_items: customItems,
    };

    // 🎯 OPTIMISTIC UPDATE
    const optimisticScenario = {
      ...currentScenario,
      ...updates,
      updated_at: new Date().toISOString(),
      _isOptimistic: true,
    };

    setCurrentScenario(optimisticScenario);
    setSavedScenarios(prev =>
      prev.map(s => s.scenario_id === currentScenarioId ? optimisticScenario : s)
    );
    markAsSaved();

    try {
      const realScenario = await updateScenarioAPI(currentScenarioId, updates);
      
      setCurrentScenario(realScenario);
      setSavedScenarios(prev =>
        prev.map(s => s.scenario_id === currentScenarioId ? realScenario : s)
      );
      
      return realScenario;
    } catch (error) {
      console.error('❌ Error updating scenario:', error);
      
      setCurrentScenario(currentScenario);
      setSavedScenarios(prev =>
        prev.map(s => s.scenario_id === currentScenarioId ? currentScenario : s)
      );
      
      alert(`Failed to update scenario: ${error.message}`);
      throw error;
    }
  }, [currentScenarioId, currentScenarioName, currentScenario, updateScenarioAPI, createScenario, markAsSaved]);

  // ============================================================================
  // DELETE SCENARIO (with optimistic update)
  // ============================================================================
  const deleteScenario = useCallback(async () => {
    if (!currentScenarioId || currentScenarioId.startsWith('temp_')) {
      console.warn('⚠️ Cannot delete baseline or temp scenario');
      return;
    }
    
    if (!window.confirm(`Delete scenario "${currentScenarioName}"?`)) {
      return;
    }
    
    const deletedScenario = currentScenario;
    const deletedId = currentScenarioId;
    
    // 🎯 OPTIMISTIC UPDATE
    setSavedScenarios(prev => prev.filter(s => s.scenario_id !== currentScenarioId));
    
    setCurrentScenarioId(null);
    setCurrentScenarioName('Actual Data');
    setCurrentScenario(null);
    setHasUnsavedChanges(false);

    try {
      await deleteScenarioAPI(deletedId);
      return { shouldReloadBaseline: true };
    } catch (error) {
      console.error('❌ Error deleting scenario:', error);
      
      setSavedScenarios(prev => [...prev, deletedScenario]);
      setCurrentScenarioId(deletedId);
      setCurrentScenarioName(deletedScenario.scenario_name);
      setCurrentScenario(deletedScenario);
      
      alert(`Failed to delete scenario: ${error.message}`);
      throw error;
    }
  }, [currentScenarioId, currentScenarioName, currentScenario, deleteScenarioAPI]);

  // ============================================================================
  // DEFAULT SCENARIO MANAGEMENT
  // ============================================================================
  const setAsDefault = useCallback(async (scenarioId) => {
    try {
      console.log('⭐ Setting default scenario:', scenarioId);
      
      // Find scenario name for alert
      const scenario = savedScenarios.find(s => s.scenario_id === scenarioId);
      const scenarioName = scenario?.scenario_name || 'this scenario';
      
      // Save to backend via the preference hook
      await setDefaultScenarioPref({ scenario_id: scenarioId });
      
      // Update scenarios list with is_default flag
      setSavedScenarios(prev => 
        prev.map(s => ({
          ...s,
          is_default: s.scenario_id === scenarioId
        }))
      );
      alert(`"${scenarioName}" set as default scenario`);
    } catch (error) {
      console.error('❌ Error setting default scenario:', error);
      alert(`Failed to set default scenario: ${error.message}`);
      throw error;
    }
  }, [setDefaultScenarioPref, savedScenarios]);

  const clearDefault = useCallback(async () => {
    try {
      console.log('🔄 Clearing default scenario');
      
      // Clear from backend
      await setDefaultScenarioPref(null);
      
      // Update scenarios list
      setSavedScenarios(prev => 
        prev.map(s => ({
          ...s,
          is_default: false
        }))
      );
      
      console.log('✅ Default scenario cleared');
    } catch (error) {
      console.error('❌ Error clearing default scenario:', error);
      alert(`Failed to clear default: ${error.message}`);
      throw error;
    }
  }, [setDefaultScenarioPref]);

  const loadDefaultScenario = useCallback(async (applyScenarioFn) => {
    // Extract scenario_id from preference value
    const scenarioId = defaultScenarioPref?.scenario_id;
    
    if (!scenarioId) {
      console.log('No default scenario set');
      return false;
    }

    try {
      console.log('📌 Loading default scenario:', scenarioId);
      await loadScenario(scenarioId, applyScenarioFn);
      return true;
    } catch (error) {
      console.error('❌ Failed to load default scenario:', error);
      return false;
    }
  }, [defaultScenarioPref, loadScenario]);

  // ============================================================================
  // SAVE MODAL HANDLERS
  // ============================================================================
  const openSaveModal = useCallback((mode = 'new') => {
    setSaveModalMode(mode);
    setShowSaveModal(true);
  }, []);

  const closeSaveModal = useCallback(() => {
    setShowSaveModal(false);
  }, []);

  const handleSaveModalSubmit = useCallback(async ({ name, description, averagingMethod, adjustments, customItems }) => {
    try {
      if (saveModalMode === 'rename') {
        await updateScenario({ 
          name, 
          description: currentScenario?.scenario_description,
          averagingMethod,
          adjustments, 
          customItems 
        });
        setCurrentScenarioName(name);
        closeSaveModal();
      } else if (saveModalMode === 'edit') {
        await updateScenario({ 
          name, 
          description,
          averagingMethod,
          adjustments, 
          customItems 
        });
        setCurrentScenarioName(name);
        closeSaveModal();
      } else {
        // Create new (both 'new' and 'saveAs')
        await createScenario({ name, description, averagingMethod, adjustments, customItems });
      }
    } catch (error) {
      console.error('❌ Error in save modal:', error);
    }
  }, [saveModalMode, currentScenario, createScenario, updateScenario, closeSaveModal]);

  // ============================================================================
  // QUICK ACTION HANDLERS
  // ============================================================================
  const handleEdit = useCallback(() => {
    openSaveModal('edit');
  }, [openSaveModal]);

  const handleSaveAsNew = useCallback(() => {
    openSaveModal('saveAs');
  }, [openSaveModal]);

  const handleRename = useCallback(() => {
    openSaveModal('rename');
  }, [openSaveModal]);

  const handleDiscardChanges = useCallback(() => {
    if (currentScenarioId && !currentScenarioId.startsWith('temp_')) {
      return { action: 'loadScenario', scenarioId: currentScenarioId };
    } else {
      return { action: 'loadActual' };
    }
  }, [currentScenarioId]);

  // ============================================================================
  // UNSAVED CHANGES DIALOG HANDLERS
  // ============================================================================
  const handleUnsavedDialogUpdate = useCallback(async ({ averagingMethod, adjustments, customItems }) => {
    try {
      await updateScenario({ 
        name: currentScenarioName,
        description: currentScenario?.scenario_description,
        averagingMethod, 
        adjustments, 
        customItems 
      });
      
      setShowUnsavedDialog(false);
      const switchTo = pendingScenarioSwitch;
      setPendingScenarioSwitch(null);
      
      return { 
        action: switchTo === null ? 'loadActual' : 'loadScenario',
        scenarioId: switchTo 
      };
    } catch (error) {
      console.error('❌ Error updating before switch:', error);
      return null;
    }
  }, [currentScenarioName, currentScenario, pendingScenarioSwitch, updateScenario]);

  const handleUnsavedDialogDiscard = useCallback(() => {
    setShowUnsavedDialog(false);
    const switchTo = pendingScenarioSwitch;
    setPendingScenarioSwitch(null);
    
    return { 
      action: switchTo === null ? 'loadActual' : 'loadScenario',
      scenarioId: switchTo 
    };
  }, [pendingScenarioSwitch]);

  const handleUnsavedDialogCancel = useCallback(() => {
    setShowUnsavedDialog(false);
    setPendingScenarioSwitch(null);
  }, []);

  // ============================================================================
  // RETURN API
  // ============================================================================
  
  return {
    // State
    currentScenarioId,
    currentScenarioName,
    currentScenario,
    hasUnsavedChanges,
    savedScenarios,
    isBaseline,
    
    // Loading states
    isLoadingScenario,
    isSavingScenario: isSavingAPI,
    
    // Modal states
    showSaveModal,
    saveModalMode,
    showUnsavedDialog,
    
    // State setters
    setCurrentScenarioId,
    setCurrentScenarioName,
    setCurrentScenario,
    setSavedScenarios,
    markAsSaved,
    markAsChanged,
    setHasUnsavedChanges,
    
    // Load/switch scenarios
    loadActual,
    loadScenario,
    newScenario,
    handleSelectScenario,

    // CRUD operations
    createScenario,
    updateScenario,
    deleteScenario,
    
    // Modal handlers
    openSaveModal,
    closeSaveModal,
    handleSaveModalSubmit,
    
    // Quick actions
    handleSaveAsNew,
    handleRename,
    handleEdit,
    handleDiscardChanges,
    
    // Unsaved changes dialog
    handleUnsavedDialogUpdate,
    handleUnsavedDialogDiscard,
    handleUnsavedDialogCancel,
    closeUnsavedDialog: () => setShowUnsavedDialog(false),
    
    // Scenarios list
    scenariosLoaded,
    loadScenariosList,
    refreshScenarios: loadScenariosList,

    // Default scenario management
    defaultScenarioId: defaultScenarioPref?.scenario_id || null,
    isLoadingDefaultScenario: loadingDefaultPref,
    setAsDefault,
    clearDefault,
    loadDefaultScenario,
  };
};